import { Box, ButtonBase, Container, styled } from '@mui/material';
import React, { useState } from 'react';
import { useInterval } from 'usehooks-ts';

import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWinner, CompetitionWithConfig } from '@/lib/api/requests.ts';
import { NumberCircle } from '@/ui/NumberCircle.tsx';
import { NumberedList, NumberedListItem } from '@/ui/NumberedList.tsx';

import { WAKE_UP_2024_CAMPAIGN_END_DATE } from '../../../config.ts';
import Bed from '../_assets/bed.svg?styled';
import Curtain from '../_assets/curtain.svg?styled';
import Rays from '../_assets/rays.svg?styled';
import Window from '../_assets/window.svg?styled';
import { CTA } from '../_components/CTA.tsx';

const dateFormatter = new Intl.DateTimeFormat('no', {
  day: 'numeric',
  month: 'long',
});

const dateStr = dateFormatter.format(WAKE_UP_2024_CAMPAIGN_END_DATE);

const Root = styled('div')(({ theme }) => ({
  background: 'linear-gradient(260.9deg, #608FDA 2.54%, #072453 95.48%)',
  width: '100%',
  maxHeight: 'calc(100vh - var(--top-bar-height))',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  height: '780px',
  minHeight: '700px',

  [theme.breakpoints.up('md')]: {
    aspectRatio: '3 / 4',
    minHeight: '800px',
  },
  [theme.breakpoints.up('lg')]: {
    aspectRatio: '16 / 9',
    height: 'auto',
    minHeight: '900px',
  },
}));

export const TopSection = (props: {
  winners: CompetitionWinner[] | undefined;
  activeCompetition: CompetitionWithConfig | undefined;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Root>
      <Window
        sx={(theme) => ({
          position: 'absolute',
          height: '55%',
          left: 600,

          [theme.breakpoints.up('lg')]: {
            left: 'auto',
            top: -111,
            right: 0,
            height: '65%',
          },
        })}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -6,
          width: 1000,
          scale: { xs: '0.7', lg: '0.85', xl: '1' },
          left: { xs: -22, md: 0 },
          transformOrigin: 'bottom left',
        }}
      >
        <Bed />
        <Box sx={{ position: 'absolute', top: 152, left: 61, color: '#FFFFFF' }}>
          <Clock />
        </Box>
      </Box>
      <Rays
        sx={(theme) => ({
          position: 'absolute',
          pointerEvents: 'none',
          height: '200%',
          bottom: -300,
          left: -100,

          [theme.breakpoints.up('md')]: {
            top: -400,
            right: -200,
          },
          [theme.breakpoints.up('lg')]: {
            height: 'auto',
          },
        })}
      />
      <Curtain
        sx={{
          position: 'absolute',
          left: { xs: 1020, lg: 'auto' },
          right: { lg: -2 },
          top: 0,
          bottom: 0,
          height: '100%',
        }}
      />
      <Container sx={{ color: 'white', mt: { xs: 6, md: 10 }, zIndex: '1' }} maxWidth="xl">
        <Box
          sx={{
            maxWidth: { xs: 370, md: 450, xl: 800 },
            mx: { xs: 'auto', sm: 0 },
            ml: { sm: 10 },
          }}
        >
          <h1 className="mb-2 text-[2.438rem] leading-[140%] font-bold text-balance xl:text-[3.812rem]">
            Er det du som våkner gjeldfri {dateStr}?
          </h1>
          <p className="mb-8 typography-v1-h4">
            Vi sletter forbruks- og kredittkortgjelden inntil 500.000 kr til én heldig vinner.
          </p>
          <p className="mb-2 typography-v1-h4">For å delta:</p>

          <NumberedList
            sx={{ mb: 4 }}
            renderNumber={(props) => (
              <NumberCircle
                sx={{
                  fontSize: 14,
                  height: 20,
                  backgroundColor: '#FBFDFF',
                  color: '#031839',
                }}
                {...props}
              />
            )}
            renderLabel={(props) => <p {...props} className="typography-v1-body3" />}
          >
            <NumberedListItem>Logg inn med BankID</NumberedListItem>
            <NumberedListItem>Sjekk din gjeld</NumberedListItem>
            <NumberedListItem>Delta i trekningen</NumberedListItem>
          </NumberedList>

          <p className="mb-6 typography-v1-h4">
            <span>Denne trekningen er avsluttet. </span>
            <ButtonBase
              onClick={() => setIsOpen(true)}
              sx={{
                color: '#74C6FD',
                textDecoration: 'underline',
                mb: 0.5,
                fontWeight: 700,
                ml: 1,
              }}
            >
              Se vinneren her
            </ButtonBase>
          </p>

          <CTA
            activeCompetition={props.activeCompetition}
            onClick={() => {
              sendUserPortalEvent('wake24_competition_cta_top_clicked');
            }}
          />
        </Box>
      </Container>

      {props.winners && (
        <CompetitionWinnersDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          winners={props.winners}
        />
      )}
    </Root>
  );
};

const formatNumber = (n: number) => n.toString().padStart(2, '0');

const Clock: React.FC = () => {
  const [now, setNow] = useState(() => new Date());
  useInterval(() => setNow(new Date()), 100);

  return (
    <span suppressHydrationWarning>
      06 : {formatNumber(now.getMinutes())} : {formatNumber(now.getSeconds())}
    </span>
  );
};
